// settingsApi — typed fetchers for /v1/me/* dashboard self-serve endpoints.

import { apiFetch } from "../../lib/api-client.js";

export interface LineStatus {
  bound: boolean;
  /** Short prefix of the LINE userId (`Uabcdef…`) — full id stays
   *  server-side because §6 marks LINE userId as sensitive. */
  line_user_id_prefix?: string;
  /** LINE display name from the most recent active line_integrations
   *  row matching this user. Null when no row found. */
  display_name?: string | null;
  /** Unix-ms when users.updated_at was last touched (proxy for "linked
   *  at" — we don't store a dedicated column). */
  linked_at?: number | null;
}

export class SettingsApiError extends Error {
  status: number; code: string; correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId;
  }
}

async function asError(r: Response): Promise<SettingsApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const b = (await r.json()) as { error?: string; correlation_id?: string };
    code = b.error ?? code; cid = b.correlation_id ?? cid;
  } catch { /* not JSON — keep defaults */ }
  return new SettingsApiError(r.status, code, cid);
}

export async function getLineStatus(): Promise<LineStatus> {
  const r = await apiFetch(`/v1/me/line-status`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as LineStatus;
}

export async function unlinkLine(): Promise<{ ok: true; was_bound: boolean }> {
  const r = await apiFetch(`/v1/me/unlink-line`, { method: "POST", credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; was_bound: boolean };
}

export interface MyRoleAssignment {
  id: string;
  role_id: string;
  role_name: string;
  role_is_system: boolean;
  scope_json: { sites?: string[]; buildings?: string[]; clients?: string[] } | null;
  permissions: string[];
}

export interface MyRoles {
  user_id: string | null;
  tenant_id: string;
  assignments: MyRoleAssignment[];
  permissions_union: string[];
}

export async function getMyRoles(): Promise<MyRoles> {
  const r = await apiFetch(`/v1/me/roles`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as MyRoles;
}

export function friendlySettingsError(err: unknown): string {
  if (err instanceof SettingsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "目前角色無權變更此設定。";
    return "暫時無法載入設定，請稍後再試。";
  }
  return "連線中斷，請檢查網路後再試。";
}
